import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import ChatBell from './ChatBell';
import ProfilePictureModal from './ProfilePictureModal';
import ChangePasswordDialog from './ChangePasswordDialog';
import StickyNotesPanel from './StickyNotesPanel';
import { useAmountVisibility } from '../contexts/AmountVisibilityContext';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { allowedPathsFor } from '../lib/permissions';
import ErrorBoundary from './ErrorBoundary';
import {
  List,
  Users,
  Briefcase,
  MapPin,
  CalendarDots,
  Receipt,
  TrendUp,
  ChartBar,
  SignOut,
  Wallet,
  CaretLeft,
  Key,
  Eye,
  EyeSlash,
  FileText,
  Package,
  Percent,
  UserCircle,
  Buildings,
  ArrowClockwise,
  Quotes,
  FilePlus,
  Phone,
  Calendar,
  Lightning,
  Clock,
  CalendarBlank,
  Kanban,
  ClipboardText,
  House,
  ListChecks,
  Megaphone,
  HardDrives,
  ChatCircle,
  Bell,
  Vault,
} from '@phosphor-icons/react';

const navSections = [
  {
    label: 'OVERVIEW',
    items: [{ path: '/dashboard', label: 'Dashboard', icon: List }],
  },
  {
    label: 'MY WORKSPACE',
    items: [
      { path: '/my/dashboard', label: 'My Dashboard', icon: House, allRoles: true },
      { path: '/my/tasks', label: 'My Tasks', icon: ListChecks, allRoles: true },
      { path: '/my/attendance', label: 'My Attendance', icon: Clock, allRoles: true },
      { path: '/my/leaves', label: 'My Leaves', icon: Calendar, allRoles: true },
      { path: '/my/permissions', label: 'My Permissions', icon: ClipboardText, allRoles: true },
    ],
  },
  {
    label: 'COMMUNICATION',
    items: [
      { path: '/chat', label: 'Chat', icon: ChatCircle, allRoles: true },
      { path: '/standup', label: 'Daily Standup', icon: Megaphone, allRoles: true },
      { path: '/client-workboard', label: 'Client Workboard', icon: Briefcase, allRoles: true },
      { path: '/reminders', label: 'Reminders', icon: Bell, allRoles: true },
    ],
  },
  {
    label: 'PROJECT MANAGEMENT',
    items: [
      { path: '/projects', label: 'Projects', icon: Briefcase, leadOrAdmin: true },
      { path: '/tasks/board', label: 'Task Board', icon: Kanban, leadOrAdmin: true },
      { path: '/permissions', label: 'Permission Board', icon: ClipboardText, leadOrAdmin: true },
    ],
  },
  {
    label: 'MASTERS',
    items: [
      { path: '/employees', label: 'Employees', icon: Users },
      { path: '/departments', label: 'Departments', icon: Briefcase },
      { path: '/designations', label: 'Designations', icon: MapPin },
    ],
  },
  {
    label: 'PAYROLL',
    items: [
      { path: '/attendance', label: 'Attendance', icon: CalendarDots },
      { path: '/checkin-dashboard', label: 'Daily Check-in', icon: Clock },
      { path: '/import-attendance', label: 'Import Attendance', icon: Receipt },
      { path: '/leaves', label: 'Leaves', icon: Calendar },
      { path: '/payroll', label: 'Payroll', icon: Wallet },
      { path: '/salary-hikes', label: 'Salary Hikes', icon: TrendUp },
    ],
  },
  {
    label: 'SALES & CRM',
    items: [
      { path: '/leads', label: 'Leads CRM', icon: Phone },
      { path: '/clients', label: 'Clients', icon: UserCircle },
      { path: '/proposals', label: 'Proposals', icon: FilePlus },
      { path: '/integrations', label: 'Lead Integrations', icon: Lightning, adminOnly: true },
    ],
  },
  {
    label: 'BILLING',
    items: [
      { path: '/invoices', label: 'Invoices', icon: FileText },
      { path: '/recurring', label: 'Recurring', icon: ArrowClockwise },
      { path: '/products', label: 'Products', icon: Package },
      { path: '/taxes', label: 'Taxes', icon: Percent },
      { path: '/companies', label: 'Companies', icon: Buildings },
      { path: '/gst-filing', label: 'GST Filing', icon: Receipt, adminOnly: true },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      { path: '/csrm', label: 'Client Success', icon: FileText },
      { path: '/marketing', label: 'Marketing Ops', icon: Megaphone },
      { path: '/appointments', label: 'Appointments', icon: CalendarBlank, allRoles: true },
      { path: '/vault', label: 'Password Vault', icon: Vault, allRoles: true },
      { path: '/personal-vault', label: 'Personal Vault', icon: Vault, adminOnly: true },
      { path: '/assets', label: 'Assets', icon: Package },
      { path: '/expenses', label: 'Expenses', icon: Receipt, allRoles: true },
      { path: '/bank-accounts', label: 'Cash Book', icon: Receipt, adminOnly: true },
      { path: '/hostings', label: 'Hosting & Domains', icon: HardDrives },
    ],
  },
  {
    label: 'REPORTS',
    items: [
      { path: '/reports', label: 'Payroll Reports', icon: ChartBar },
      { path: '/attendance-report', label: 'Attendance Report', icon: ChartBar, adminOnly: true },
      { path: '/invoice-reports', label: 'Invoice Reports', icon: ChartBar },
      { path: '/tds-report', label: 'TDS Report', icon: ChartBar, adminOnly: true },
      { path: '/profit-loss', label: 'Profit & Loss', icon: TrendUp, adminOnly: true },
      { path: '/productivity', label: 'Productivity', icon: TrendUp, adminOnly: true },
      { path: '/time-tracking', label: 'Time Tracking', icon: ChartBar, adminOnly: true },
      { path: '/coordinator', label: 'Coordinator Board', icon: ChartBar, adminOnly: true },
    ],
  },
  {
    label: 'SETTINGS',
    items: [
      { path: '/users', label: 'Users', icon: Users, adminOnly: true },
    ],
  },
];

// Allowed paths per non-admin role (delegated to lib/permissions.js — kept here as legacy reference only)
// const ROLE_PATHS = { ... };  // moved to /app/frontend/src/lib/permissions.js

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/employees': 'Employee Master',
  '/departments': 'Department Master',
  '/designations': 'Designation Master',
  '/attendance': 'Attendance Entry',
  '/import-attendance': 'Import Attendance',
  '/attendance-report': 'Attendance Reports',
  '/clients': 'Clients',
  '/products': 'Products & Services',
  '/taxes': 'Tax Rates',
  '/invoices': 'Invoices',
  '/csrm': 'Client Success (CSRM)',
  '/marketing': 'Digital Marketing Operations',
  '/invoice-reports': 'Invoice Reports',
  '/tds-report': 'TDS Deduction Report',
  '/companies': 'Companies',
  '/quotes': 'Quotes',
  '/recurring': 'Recurring Invoices',
  '/proposals': 'Proposal Pipeline',
  '/proposals/builder': 'Proposal Builder',
  '/payroll': 'Payroll',
  '/salary-hikes': 'Salary Hikes',
  '/expenses': 'Expenses',
  '/reports': 'Reports',
  '/users': 'Users & Roles',
  '/leads': 'Leads CRM',
  '/leaves': 'Leave Management',
  '/integrations': 'Lead Integrations',
  '/checkin-dashboard': 'Daily Check-in Dashboard',
  '/assets': 'Assets Management',
  '/appointments': 'Appointments & Meetings',
  '/projects': 'Projects',
  '/tasks/board': 'Task Board',
  '/standup': 'Daily Standup',
  '/client-workboard': 'Client Workboard',
  '/hostings': 'Hosting & Domains',
  '/chat': 'Internal Chat',
  '/permissions': 'Permission Board',
  '/permission-request': 'Request Permission',
  '/productivity': 'Productivity Dashboard',
  '/time-tracking': 'Time Tracking — Hours per Task & Client',
  '/my/dashboard': 'My Workspace',
  '/my/tasks': 'My Tasks',
  '/my/attendance': 'My Attendance',
  '/my/leaves': 'My Leaves',
  '/my/permissions': 'My Permission Requests',
  '/reminders': 'Admin Dependency Reminders',
  '/vault': 'Password Vault',
  '/personal-vault': 'Personal Vault',
  '/gst-filing': 'GST Filing (GSTR-1 & GSTR-3B)',
  '/profit-loss': 'Profit & Loss',
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const { hidden: amountsHidden, toggle: toggleAmounts } = useAmountVisibility();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileSaved = async (newPic) => {
    // Refresh /auth/me so the new picture flows into the sidebar everywhere
    try { await refreshUser?.(); } catch (_) { /* refreshUser may not exist */ }
  };

  const pageTitle = PAGE_TITLES[location.pathname] || '';
  const userInitial = (user?.name || 'U').charAt(0).toUpperCase();
  const userPic = user?.profile_picture || '';

  // Filter sidebar based on the logged-in user's role + extra module flags
  const filteredNav = (() => {
    if (user?.role === 'admin') {
      // Admin sees everything. Hide MY WORKSPACE for admin to avoid duplication; they have full Attendance/Leaves modules.
      return navSections.filter((s) => s.label !== 'MY WORKSPACE');
    }
    const allowed = allowedPathsFor(user) || new Set();
    return navSections
      .map((s) => ({ ...s, items: s.items.filter((i) => allowed.has(i.path) && !i.adminOnly) }))
      .filter((s) => s.items.length > 0);
  })();

  return (
    <div className="flex h-screen" style={{ background: 'var(--dx-bg)' }}>
      <aside
        className={`transition-all duration-300 flex flex-col h-screen overflow-hidden`}
        style={{
          background: 'var(--dx-sidebar)',
          width: sidebarOpen ? '228px' : '64px',
          minWidth: sidebarOpen ? '228px' : '64px',
        }}
        data-testid="sidebar"
      >
        {/* Logo / brand */}
        <div className="px-4 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          {sidebarOpen && (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/logo-v8-black.png`}
              alt="Dextra"
              className="object-contain"
              style={{ filter: 'brightness(0) invert(1)', height: '44px', maxWidth: '170px' }}
              data-testid="sidebar-logo"
            />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white/60 hover:text-white p-1 rounded transition-colors"
            data-testid="sidebar-toggle-btn"
            title={sidebarOpen ? 'Collapse' : 'Expand'}
          >
            <CaretLeft size={16} className={`transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* User block */}
        {sidebarOpen && (
          <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <button
              onClick={() => setProfileModalOpen(true)}
              className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-bold text-white flex-shrink-0 hover:ring-2 hover:ring-white/30 transition-shadow"
              style={{ background: 'var(--dx-primary)' }}
              data-testid="sidebar-avatar-btn"
              title="Change profile picture"
            >
              {userPic ? (
                <img src={userPic} alt="" className="w-full h-full object-cover" data-testid="sidebar-avatar-img" />
              ) : (
                userInitial
              )}
            </button>
            <div className="overflow-hidden">
              <div className="text-[13px] text-white font-medium leading-tight truncate" data-testid="sidebar-user-name">
                {user?.name || 'User'}
              </div>
              <div className="text-[10px] text-white/40 leading-tight uppercase tracking-wider">
                {user?.role || 'staff'}
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {filteredNav.map((section) => (
            <div key={section.label} className="mb-1">
              {sidebarOpen && (
                <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-white/20">
                  {section.label}
                </div>
              )}
              <ul>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        className="flex items-center gap-3 px-4 py-2.5 text-[13px] transition-all"
                        style={{
                          color: isActive ? '#ffffff' : 'var(--dx-sidebar-text)',
                          background: isActive ? 'rgba(29,158,117,0.18)' : 'transparent',
                          borderLeft: `3px solid ${isActive ? 'var(--dx-primary)' : 'transparent'}`,
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                            e.currentTarget.style.color = '#ffffff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--dx-sidebar-text)';
                          }
                        }}
                      >
                        <Icon size={16} weight={isActive ? 'bold' : 'regular'} />
                        {sidebarOpen && <span>{item.label}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer / Logout + Change Password */}
        <div className="px-4 py-3 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button
            onClick={() => setShowChangePwd(true)}
            data-testid="change-pwd-btn"
            className="flex items-center gap-2 text-[13px] text-white/60 hover:text-white transition-colors w-full"
            title="Change my password"
          >
            <Key size={15} />
            {sidebarOpen && <span>Change Password</span>}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-[13px] text-white/60 hover:text-white transition-colors w-full"
          >
            <SignOut size={15} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <ChangePasswordDialog open={showChangePwd} onClose={() => setShowChangePwd(false)} />

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <div
          className="px-6 flex items-center justify-between flex-shrink-0"
          style={{
            height: '54px',
            background: 'var(--dx-surface)',
            borderBottom: '1px solid var(--dx-border)',
          }}
        >
          <div className="text-[15px] font-bold" style={{ color: 'var(--dx-text)' }} data-testid="topbar-title">
            {pageTitle}
          </div>
          <div id="topbar-actions" className="flex items-center gap-2">
            <WorkspaceSwitcher />
            <button
              onClick={toggleAmounts}
              data-testid="amount-visibility-toggle"
              title={amountsHidden ? 'Show financial amounts' : 'Hide financial amounts (so people near you can\'t see)'}
              className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${amountsHidden ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              {amountsHidden ? <EyeSlash size={16} weight="fill" /> : <Eye size={16} />}
            </button>
            <ChatBell />
            <NotificationBell />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="content-area">
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      </main>

      {/* Profile picture modal — open from sidebar avatar */}
      <ProfilePictureModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        currentPicture={userPic}
        onSaved={handleProfileSaved}
      />

      {/* Sticky Notes — collapsible right-side panel, available on every page */}
      <StickyNotesPanel />
    </div>
  );
};
